import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";
import { TrendingUp, AlertOctagon, ArrowUpRight, BarChart3 } from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Cell,
} from "recharts";

function Stat({ label, value, sub, tone = "ink", testid }) {
    const toneColor =
        tone === "red"
            ? "text-brand-red"
            : tone === "green"
              ? "text-brand-green"
              : tone === "blue"
                ? "text-brand-blue"
                : "text-brand-ink";
    return (
        <div className="stat-card" data-testid={testid}>
            <div className="overline mb-4">{label}</div>
            <div className={`font-display font-black text-4xl leading-none tracking-tight ${toneColor}`}>
                ₺ <span className="font-mono-num">{value}</span>
            </div>
            {sub && <div className="text-xs text-neutral-500 font-mono mt-3">{sub}</div>}
        </div>
    );
}

const AGING_COLORS = ["#0A0A0A", "#FFC107", "#F57C00", "#E53935", "#94A3B8"];

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [series, setSeries] = useState(null);
    const [aging, setAging] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/dashboard/stats"),
            api.get("/dashboard/timeseries"),
            api.get("/dashboard/aging"),
        ])
            .then(([s, t, a]) => {
                setData(s.data);
                setSeries(t.data.series);
                setAging(a.data.buckets);
            })
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen">
            <header className="border-b border-black/10 px-10 py-6 flex items-center justify-between">
                <div>
                    <div className="overline">Bölüm 01</div>
                    <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">
                        Kontrol Paneli
                    </h1>
                </div>
                <div className="text-right">
                    <div className="overline">Bugün</div>
                    <div className="font-mono text-sm">
                        {new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}
                    </div>
                </div>
            </header>

            {loading || !data ? (
                <div className="p-10 font-mono text-xs uppercase text-neutral-500">Veriler yükleniyor…</div>
            ) : (
                <>
                    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-b border-black/10">
                        <Stat testid="stat-customers" label="Toplam Müşteri" value={data.total_customers} sub="Aktif kart adedi" />
                        <Stat testid="stat-invoiced" label="Toplam Faturalanan" value={formatMoney(data.total_invoiced)} sub="Tüm dönem" />
                        <Stat testid="stat-collected" label="Toplam Tahsil Edilen" value={formatMoney(data.total_collected)} sub="Tüm dönem" tone="green" />
                        <Stat testid="stat-outstanding" label="Vadesi Bekleyen" value={formatMoney(data.outstanding)} sub="Açık cari toplamı" tone={data.outstanding > 0 ? "red" : "ink"} />
                    </section>

                    {/* Charts */}
                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-b border-black/10">
                        <div className="p-8 border-b lg:border-b-0 lg:border-r border-black/10">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="overline">Son 6 Ay</div>
                                    <h2 className="font-display text-2xl font-bold tracking-tight mt-1">
                                        Fatura vs Tahsilat
                                    </h2>
                                </div>
                                <BarChart3 size={18} className="text-brand-blue" />
                            </div>
                            <div style={{ width: "100%", height: 260 }} data-testid="chart-timeseries">
                                <ResponsiveContainer>
                                    <BarChart data={series || []} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="0" stroke="#eee" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#0A0A0A" }} tickLine={false} />
                                        <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                                        <Tooltip
                                            cursor={{ fill: "rgba(0,47,167,0.06)" }}
                                            contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", fontSize: 12, fontFamily: "IBM Plex Mono" }}
                                            formatter={(v) => "₺ " + formatMoney(v)}
                                        />
                                        <Bar dataKey="faturalanan" name="Faturalanan" fill="#0A0A0A" />
                                        <Bar dataKey="tahsilat" name="Tahsilat" fill="#002FA7" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="p-8">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="overline">Vade Analizi</div>
                                    <h2 className="font-display text-2xl font-bold tracking-tight mt-1">
                                        Yaşlandırma
                                    </h2>
                                </div>
                                <AlertOctagon size={18} className="text-brand-red" />
                            </div>
                            <div style={{ width: "100%", height: 260 }} data-testid="chart-aging">
                                <ResponsiveContainer>
                                    <BarChart data={aging || []} layout="vertical" margin={{ top: 6, right: 20, left: 30, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="0" stroke="#eee" horizontal={false} />
                                        <XAxis type="number" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                                        <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#0A0A0A" }} tickLine={false} width={110} />
                                        <Tooltip
                                            cursor={{ fill: "rgba(0,47,167,0.06)" }}
                                            contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", fontSize: 12, fontFamily: "IBM Plex Mono" }}
                                            formatter={(v) => "₺ " + formatMoney(v)}
                                        />
                                        <Bar dataKey="amount" name="Tutar">
                                            {(aging || []).map((_, idx) => (
                                                <Cell key={idx} fill={AGING_COLORS[idx % AGING_COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </section>

                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                        <div className="p-8 border-b lg:border-b-0 lg:border-r border-black/10">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="overline">Bakiye Sıralaması</div>
                                    <h2 className="font-display text-2xl font-bold tracking-tight mt-1">
                                        En Yüksek Borçlu Müşteriler
                                    </h2>
                                </div>
                                <AlertOctagon size={18} className="text-brand-red" />
                            </div>
                            {data.top_debtors.length === 0 ? (
                                <div className="text-sm text-neutral-500 font-mono">Açık bakiyeli müşteri yok.</div>
                            ) : (
                                <table className="w-full text-sm" data-testid="top-debtors-table">
                                    <thead>
                                        <tr className="border-b border-black/10">
                                            <th className="text-left overline py-2">Müşteri</th>
                                            <th className="text-right overline py-2">Bakiye (₺)</th>
                                            <th className="w-8"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.top_debtors.map((d) => (
                                            <tr key={d.id} className="border-b border-black/5">
                                                <td className="py-3">{d.name}</td>
                                                <td className="py-3 text-right font-mono-num text-brand-red">
                                                    {formatMoney(d.balance)}
                                                </td>
                                                <td className="py-3 text-right">
                                                    <Link to={`/musteriler/${d.id}`} className="text-brand-blue hover:underline">
                                                        <ArrowUpRight size={16} />
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="p-8">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="overline">Son 30 Gün</div>
                                    <h2 className="font-display text-2xl font-bold tracking-tight mt-1">
                                        Tahsilatı Yapan Kişi
                                    </h2>
                                </div>
                                <TrendingUp size={18} className="text-brand-green" />
                            </div>
                            {data.by_collector.length === 0 ? (
                                <div className="text-sm text-neutral-500 font-mono">Bu dönemde tahsilat yok.</div>
                            ) : (
                                <table className="w-full text-sm" data-testid="collectors-table">
                                    <thead>
                                        <tr className="border-b border-black/10">
                                            <th className="text-left overline py-2">Tahsilatçı</th>
                                            <th className="text-right overline py-2">Adet</th>
                                            <th className="text-right overline py-2">Toplam (₺)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.by_collector.map((c) => (
                                            <tr key={c.collector_id || c.collector_name} className="border-b border-black/5">
                                                <td className="py-3">{c.collector_name || "-"}</td>
                                                <td className="py-3 text-right font-mono-num">{c.count}</td>
                                                <td className="py-3 text-right font-mono-num text-brand-green">
                                                    {formatMoney(c.total)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
